import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { VipDinerService } from '../../../services/vip-diner.service';
import { CustomerService } from '../../../services/customer.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-vip-diner-detail',
  templateUrl: './vip-diner-detail.component.html',
  styleUrls: ['./vip-diner-detail.component.css'],
})
export class VipDinerDetailComponent implements OnInit {
  vipDinerId: any;
  vipDiner: any;
  loading = true;
  error: string | null = null;
  token: string = '';

  // User selection properties
  search: string = '';
  searchResults: any[] = [];
  searchLoading = false;
  searchCount = 0;
  selectedUsers: any[] = [];
  reservationUsers: any[] = [];
  managingUsers = false;
  isLive = false;
  updatingLiveStatus = false;

  // Filter properties
  showFilters = false;
  activeFilterChips: any[] = [];
  selectedGender: string = '';
  activeFilterSection: string = '';

  // Filter options
  selectedFilters: any = {
    religion: '',
    ageRange: { min: '', max: '' },
    nationality: '',
    maritalStatus: '',
    gender: '',
    interest: '',
    values: '',
    alcoholConsumption: '',
    smoker: '',
    children: '',
    iUsuallyWakeUpAt: '',
    hobbies: '',
    fitness: '',
    personality: '',
    lifeGoals: '',
    maxionGoals: '',
    meetVibes: '',
    signupStepNo: { min: '', max: '' },
    passportVerified: '',
    subscriptionStatus: '',
    noMatchesInLastDays: '',
  };

  // Filter options data
  religionOptions: string[] = [];
  nationalityOptions: string[] = [];
  maritalStatusOptions: string[] = [];
  genderOptions: string[] = [];
  interestOptions: any[] = [];
  valueOptions: any[] = [];
  alcoholConsumptionOptions: any[] = [];
  smokingOptions: any[] = [];
  childrenOptions: string[] = [];
  wakeupAtOptions: any[] = [];
  hobbiesOptions: any[] = [];
  fitnessOptions: any[] = [];
  personalityOptions: any[] = [];
  lifeGoalsOptions: any[] = [];
  maxionGoalsOptions: any[] = [];
  meetVibesOptions: any[] = [];
  subscriptionStatusOptions: string[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private vipDinerService: VipDinerService,
    private customerService: CustomerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.token = localStorage.getItem('token') || '';
    this.vipDinerId = this.route.snapshot.paramMap.get('id');

    if (this.vipDinerId) {
      this.loadVipDinerDetails();
    }
  }

  loadVipDinerDetails(): void {
    this.loading = true;
    this.error = null;

    this.vipDinerService
      .getVipDinerReservationById(Number(this.vipDinerId), this.token)
      .subscribe({
        next: (response) => {
          this.vipDiner = response.data;
          // Set the initial public/private status
          this.isLive = this.vipDiner.is_public || false;

          // Load existing users from the reservation
          this.loadExistingUsers();

          this.loading = false;
        },
        error: (error) => {
          this.error =
            error.error?.message || 'Failed to load VIP diner details';
          this.loading = false;
        },
      });
  }

  loadExistingUsers(): void {
    this.reservationUsers = [];

    // Add male user if exists
    if (this.vipDiner.male_user_id && this.vipDiner.male_user_name) {
      this.reservationUsers.push({
        id: this.vipDiner.male_user_id,
        name: this.vipDiner.male_user_name,
        gender: 'Male',
        email: this.vipDiner.male_user_email, // API doesn't return email
        phone: this.vipDiner.male_user_phone, // API doesn't return phone
      });
    }

    // Add female user if exists
    if (this.vipDiner.female_user_id && this.vipDiner.female_user_name) {
      this.reservationUsers.push({
        id: this.vipDiner.female_user_id,
        name: this.vipDiner.female_user_name,
        gender: 'Female',
        email: this.vipDiner.female_user_email, // API doesn't return email
        phone: this.vipDiner.female_user_phone, // API doesn't return phone
      });
    }
  }

  goBack(): void {
    this.router.navigate(['/vip-diner']);
  }

  editVipDiner(): void {
    // TODO: Implement edit functionality
    this.toastr.info('Edit functionality coming soon');
  }

  // User selection methods
  onKeyUpEvent(event: any): void {
    const searchTerm = event.target.value.trim();
    this.search = searchTerm;

    if (searchTerm.length >= 2) {
      this.searchUsers();
    } else {
      this.searchResults = [];
      this.searchCount = 0;
    }
  }

  searchUsers(): void {
    this.searchLoading = true;

    // Clean up filters to remove empty values
    const cleanedFilters = this.cleanFilters(this.selectedFilters);

    let obj = {
      limit: 50, // Get more users at once since no pagination
      offset: 0,
      search: this.search,
      action: 'approved', // Only search approved users
      ...cleanedFilters,
    };

    this.customerService.getCustomerList(obj, this.token).subscribe({
      next: (res: any) => {
        if (res && res.status === 200) {
          this.searchCount = res.data.total_count;
          this.searchResults = res.data.data.map((user: any) => ({
            id: user.id,
            name: user.name,
            email: user.email,
            phone: user.phone,
            age: user.age,
            gender: user.gender,
            // Add any other fields you need
          }));
        } else {
          this.toastr.error(res.message || 'Failed to search users');
          this.searchResults = [];
          this.searchCount = 0;
        }
        this.searchLoading = false;
      },
      error: (error) => {
        this.toastr.error('Failed to search users');
        this.searchResults = [];
        this.searchCount = 0;
        this.searchLoading = false;
      },
    });
  }

  // Clean up filters to remove empty values
  private cleanFilters(filters: any): any {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(filters)) {
      if (value !== '' && value !== null && value !== undefined) {
        if (typeof value === 'object' && value !== null) {
          const nestedCleaned = this.cleanFilters(value);
          if (Object.keys(nestedCleaned).length > 0) {
            cleaned[key] = nestedCleaned;
          }
        } else {
          cleaned[key] = value;
        }
      }
    }
    return cleaned;
  }

  toggleUserSelection(userId: number): void {
    const index = this.selectedUsers.findIndex((user) => user.id === userId);
    if (index > -1) {
      this.selectedUsers.splice(index, 1);
    } else {
      const user = this.searchResults.find((u) => u.id === userId);
      if (user) {
        // Check if we already have a user of the same gender selected
        const existingUserOfSameGender = this.selectedUsers.find(
          (selectedUser) => selectedUser.gender === user.gender
        );

        if (existingUserOfSameGender) {
          this.toastr.warning(
            `You can only select one ${user.gender.toLowerCase()} user at a time.`
          );
          return;
        }

        // Check if we already have a user of the same gender in the reservation
        const existingUserInReservation = this.reservationUsers.find(
          (reservationUser) => reservationUser.gender === user.gender
        );

        if (existingUserInReservation) {
          this.toastr.warning(
            `A ${user.gender.toLowerCase()} user is already in the reservation.`
          );
          return;
        }

        this.selectedUsers.push(user);
      }
    }
  }

  isUserSelected(userId: number): boolean {
    return this.selectedUsers.some((user) => user.id === userId);
  }

  canSelectUser(userId: number): boolean {
    const user = this.searchResults.find((u) => u.id === userId);
    if (!user) return false;

    // Check if user is already selected
    if (this.isUserSelected(userId)) return true;

    // Check if we already have a user of the same gender selected
    const existingUserOfSameGender = this.selectedUsers.find(
      (selectedUser) => selectedUser.gender === user.gender
    );

    if (existingUserOfSameGender) return false;

    // Check if we already have a user of the same gender in the reservation
    const existingUserInReservation = this.reservationUsers.find(
      (reservationUser) => reservationUser.gender === user.gender
    );

    if (existingUserInReservation) return false;

    return true;
  }

  selectAllUsers(): void {
    // Find one male and one female user from search results
    const maleUser = this.searchResults.find((user) => user.gender === 'Male');
    const femaleUser = this.searchResults.find(
      (user) => user.gender === 'Female'
    );

    this.selectedUsers = [];

    if (maleUser) {
      this.selectedUsers.push(maleUser);
    }

    if (femaleUser) {
      this.selectedUsers.push(femaleUser);
    }
  }

  deselectAllUsers(): void {
    this.selectedUsers = [];
  }

  addSelectedUsersToReservation(): void {
    // Filter users to ensure only one male and one female
    const maleUsers = this.selectedUsers.filter(
      (user) => user.gender === 'Male'
    );
    const femaleUsers = this.selectedUsers.filter(
      (user) => user.gender === 'Female'
    );

    // Check if we already have users of the same gender in reservation
    const existingMale = this.reservationUsers.find(
      (user) => user.gender === 'Male'
    );
    const existingFemale = this.reservationUsers.find(
      (user) => user.gender === 'Female'
    );

    let usersToAdd = [];

    // Add male user if we don't have one and selected users include a male
    if (!existingMale && maleUsers.length > 0) {
      usersToAdd.push(maleUsers[0]); // Take the first male user
    }

    // Add female user if we don't have one and selected users include a female
    if (!existingFemale && femaleUsers.length > 0) {
      usersToAdd.push(femaleUsers[0]); // Take the first female user
    }

    if (usersToAdd.length === 0) {
      this.toastr.warning(
        'No valid users to add. Only one male and one female user are allowed.'
      );
      return;
    }

    // Add users one by one to call API for each
    usersToAdd.forEach((user) => {
      this.addUserToReservation(user);
    });

    this.selectedUsers = [];
    this.searchResults = [];
    this.search = '';
  }

  addUserToReservation(user: any): void {
    if (!this.isUserInReservation(user.id)) {
      // Check if we already have a user of the same gender
      const existingUserOfSameGender = this.reservationUsers.find(
        (existingUser) => existingUser.gender === user.gender
      );

      if (existingUserOfSameGender) {
        this.toastr.warning(
          `A ${user.gender.toLowerCase()} user is already in the reservation. Only one ${user.gender.toLowerCase()} user is allowed.`
        );
        return;
      }

      this.managingUsers = true;

      this.vipDinerService
        .manageVipDinerUser(
          Number(this.vipDinerId),
          user.id,
          user.gender,
          'add',
          this.token
        )
        .subscribe({
          next: (response) => {
            this.managingUsers = false;
            if (response && response.status === 200) {
              // Refresh the VIP diner details to get updated user list
              this.loadVipDinerDetails();
              this.toastr.success(
                response.message || `${user.name} added to reservation`
              );
            } else {
              this.toastr.error(
                response.message || 'Failed to add user to reservation'
              );
            }
          },
          error: (error) => {
            this.managingUsers = false;
            this.toastr.error(
              error.error?.message || 'Failed to add user to reservation'
            );
          },
        });
    } else {
      this.toastr.warning(`${user.name} is already in the reservation`);
    }
  }

  removeUserFromReservation(user: any): void {
    this.managingUsers = true;

    this.vipDinerService
      .manageVipDinerUser(
        Number(this.vipDinerId),
        user.id,
        user.gender,
        'remove',
        this.token
      )
      .subscribe({
        next: (response) => {
          this.managingUsers = false;
          if (response && response.status === 200) {
            // Refresh the VIP diner details to get updated user list
            this.loadVipDinerDetails();
            this.toastr.success(
              response.message || `${user.name} removed from reservation`
            );
          } else {
            this.toastr.error(
              response.message || 'Failed to remove user from reservation'
            );
          }
        },
        error: (error) => {
          this.managingUsers = false;
          this.toastr.error(
            error.error?.message || 'Failed to remove user from reservation'
          );
        },
      });
  }

  isUserInReservation(userId: number): boolean {
    // Check if user is in the current reservation users list
    const inCurrentList = this.reservationUsers.some(
      (user) => user.id === userId
    );

    // Also check if user is already assigned in the API response
    const inApiResponse =
      this.vipDiner.male_user_id === userId ||
      this.vipDiner.female_user_id === userId;

    return inCurrentList || inApiResponse;
  }

  closeSearchResults(): void {
    this.searchResults = [];
    this.search = '';
    this.selectedUsers = [];
  }

  toggleLiveStatus(): void {
    const newStatus = !this.isLive;
    const action = newStatus ? 'public' : 'private';

    const formData = new FormData();
    formData.append('action', action);

    this.updatingLiveStatus = true;

    this.vipDinerService
      .updateVipDinerReservation(Number(this.vipDinerId), formData, this.token)
      .subscribe({
        next: (response) => {
          this.updatingLiveStatus = false;
          if (response && response.status === 200) {
            this.isLive = newStatus;
            this.toastr.success(
              response.message ||
                `VIP Diner ${newStatus ? 'made public' : 'made private'}`
            );
          } else {
            this.toastr.error(
              response.message || 'Failed to update VIP diner status'
            );
          }
        },
        error: (error) => {
          this.updatingLiveStatus = false;
          this.toastr.error(
            error.error?.message || 'Failed to update VIP diner status'
          );
        },
      });
  }

  // Filter methods
  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  setGenderFilter(gender: string): void {
    this.selectedGender = this.selectedGender === gender ? '' : gender;
    this.updateFilterChips();
  }

  toggleFilterSection(section: string): void {
    this.activeFilterSection =
      this.activeFilterSection === section ? '' : section;
  }

  updateFilterChips(): void {
    this.activeFilterChips = [];

    if (this.selectedGender) {
      this.activeFilterChips.push({
        label: 'Gender',
        value: this.selectedGender,
      });
    }

    // Add other filter chips as needed
  }

  clearFilterChip(chip: any): void {
    const index = this.activeFilterChips.findIndex(
      (c) => c.label === chip.label
    );
    if (index > -1) {
      this.activeFilterChips.splice(index, 1);
    }

    // Clear corresponding filter
    if (chip.label === 'Gender') {
      this.selectedGender = '';
    }
  }

  clearAllFilters(): void {
    this.activeFilterChips = [];
    this.selectedGender = '';
    this.selectedFilters = {
      religion: '',
      ageRange: { min: '', max: '' },
      nationality: '',
      maritalStatus: '',
      gender: '',
      interest: '',
      values: '',
      alcoholConsumption: '',
      smoker: '',
      children: '',
      iUsuallyWakeUpAt: '',
      hobbies: '',
      fitness: '',
      personality: '',
      lifeGoals: '',
      maxionGoals: '',
      meetVibes: '',
      signupStepNo: { min: '', max: '' },
      passportVerified: '',
      subscriptionStatus: '',
      noMatchesInLastDays: '',
    };
  }

  applyFilters(): void {
    this.updateFilterChips();
    this.searchUsers();
    this.showFilters = false;
  }

  // User details modal
  openUserDetailsModal(user: any, context: string): void {
    // TODO: Implement user details modal
    this.toastr.info(`Viewing details for ${user.name}`);
  }

  // Helper methods
  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'status-badge confirmed';
      case 'pending':
        return 'status-badge pending';
      case 'cancelled':
        return 'status-badge cancelled';
      case 'completed':
        return 'status-badge completed';
      default:
        return 'status-badge';
    }
  }

  formatDate(date: string): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  formatTime(time: string): string {
    if (!time) return 'N/A';
    return time;
  }
}
